import {CommonModule} from "@angular/common";
import {Component, computed, effect, inject, OnInit, signal} from "@angular/core";
import {MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {MatSortModule, Sort, SortDirection} from '@angular/material/sort';
import {MatTableModule} from "@angular/material/table";
import {CommonActions} from "@app/store/common/common.actions";
import {CommonState} from "@app/store/common/common.state";
import {AssociateQuestionnaireComponent, AssociateQuestionnaireConfig} from "@doctor-panel-modules/patient-personal-data/components/add-questionnaire/add-questionnaire.component";
import {IonicModule, ModalController} from "@ionic/angular";
import {QuestionnaireTemplatesState} from "@modules/questionnaires/store/questionnaire-templates.state";
import {QuestionnaireActions} from "@modules/questionnaires/store/questionnaire.actions";
import {actionsExecuting} from "@ngxs-labs/actions-executing";
import {Store} from "@ngxs/store";
import {GetTemplatesInput, QuestionnaireTemplate} from "@trec/api";
import {C3GetIconModule} from "@trec/components/get-icon";
import {C3TooltipModule} from "@trec/components/tooltip";

interface TableState {
  id: "BY_PATIENT",
  page: number;
  size: number;
  sortBy?: string;
  sortDirection?: SortDirection;
}

type FormattedQuestionnaireItem = QuestionnaireTemplate & {modelName: string}

interface FrequencyOption {
  singular: string;
  plural: string;
  value: 'DAYS' | 'WEEKS' | 'MONTHS';
}

const frequencies: FrequencyOption[] = [
  {value: 'DAYS', singular: 'Giorno', plural: 'Giorni'},
  {value: 'WEEKS', singular: 'Settimana', plural: 'Settimane'},
  {value: 'MONTHS', singular: 'Mese', plural: 'Mesi'}
];

@Component({
  selector: "app-config-questionnaires",
  templateUrl: "./config-questionnaires.component.html",
  styleUrls: ["./config-questionnaires.component.scss"],
  imports: [
    CommonModule,
    C3GetIconModule,
    IonicModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    C3TooltipModule
  ]
})
export class ConfigQuestionnairesComponent implements OnInit {
  private store = inject(Store);

  displayedColumns = [
    'NAME',
    'startDate',
    'endDate',
    'frequency',
    // 'subcontext',
    // 'subgroups',
    'status',
  ];


  private modalCtrl = inject(ModalController)
  readonly loading = this.store.selectSignal(actionsExecuting([CommonActions.FetchPatientQuestionnaires, QuestionnaireActions.GetQuestionnaireModels]))

  readonly patient = this.store.selectSignal(CommonState.patient);
  readonly items = this.store.selectSignal(CommonState.patientQuestionnaires);
  readonly total = this.store.selectSignal(CommonState.patientQuestionnairesTotalElements);

  readonly models = this.store.selectSignal(QuestionnaireTemplatesState.models);
  readonly userContextId = this.store.selectSignal(CommonState.currentContext)
  readonly userStructuretId = this.store.selectSignal(CommonState.currentStructureId)

  readonly tableState = signal<TableState>({
    page: 0,
    size: 10,
    id: "BY_PATIENT",
    //sortBy: 'nome',
    //sortDirection: 'asc'
  });

  readonly query = computed<GetTemplatesInput | null>(() => {
    const trecUserId = this.patient()?.trecUserId;
    if (trecUserId == null) {
      return null;
    }
    const tableState = this.tableState();
    return {
      trecUserId,
      active: true,
      ...tableState
    };
  });

  readonly rows = computed<FormattedQuestionnaireItem[]>(() => {
    const items = this.items();
    const models = this.models();

    return items.map((item) => {
      const model = models.find(({id}) => id === item.modelId);
      return {
        ...item,
        modelName: model ? model.name : '',
        frequencyLabel: this.formatFrequency(item.frequency, item.timeUnit)
      }
    })
  });

  constructor() {
    effect(() => {
      const query = this.query();

      if (query === null) {
        return;
      }

      this.load(query)
    })

    effect(() => {
      const context = this.userContextId();
      const structure = this.userStructuretId();

      if (!context || !structure) {
        return;
      }

      return this.store.dispatch(new QuestionnaireActions.GetQuestionnaireModels({
        creatorContextId: context,
        creatorStructureId: structure
      }))
    })
  }

  ngOnInit(): void {
  }

  private formatFrequency(
    frequency: number,
    unita: FrequencyOption['value']
  ): string {
    const option = frequencies.find(item => item.value === unita);

    if (!option) {
      return '';
    }

    const label = frequency === 1
      ? option.singular
      : option.plural;

    return `${frequency} ${label}`;
  }

  onPageChange(event: PageEvent): void {
    this.tableState.update(state => ({
      ...state,
      page: event.pageIndex,
      size: event.pageSize
    }));
  }

  onSortChange(sort: Sort): void {
    this.tableState.update(state => ({
      ...state,
      page: 0,
      sortBy: sort.active,
      sortDirection: sort.direction
    }));
  }

  loadRetry(): void {
    const query = this.query();
    if (query) {
      this.load(query)
    }
  }

  private load(query: GetTemplatesInput) {
    this.store.dispatch(new CommonActions.FetchPatientQuestionnaires(query)).subscribe({
      next: () => console.log('Dispatch completata'),
      error: err => console.error('Errore nella dispatch:', err)
    });
  }

  async onQuestionnaireAddClick() {
    const patientId = this.store.selectSnapshot(CommonState.patientId);

    const props = {
      id: "TO_PATIENT",
      patientId: patientId || '',
      templateId: null,
    } satisfies AssociateQuestionnaireConfig;
    const modal = await this.modalCtrl.create({
      component: AssociateQuestionnaireComponent,
      componentProps: {
        config: props
      },
      cssClass: "c3-modal-width-sm c3-modal-height-md",
    });
    await modal.present();
    const {role} = await modal.onDidDismiss();
    if (role === 'save') {
      const query = this.query();
      if (query) {
        this.load(query);
      }
    }
  }

}